import { useState } from 'react'
import { X, Folder } from 'lucide-react'
import { nanoid } from 'nanoid'
import { useAppStore } from '../store'
import { Modal, useModalClose } from './ui/Modal'
import { gradientFor, gradientStyle } from '../lib/folderColors'

interface FolderEditorProps {
  folderId: string | null
  onClose: () => void
}

export function FolderEditor(props: FolderEditorProps) {
  return (
    <Modal
      onClose={props.onClose}
      label={props.folderId ? 'Rename folder' : 'New folder'}
      glow={gradientFor(props.folderId)[0]}
      maxWidth={480}
    >
      <FolderBody {...props} />
    </Modal>
  )
}

function FolderBody({ folderId }: FolderEditorProps) {
  const close = useModalClose()
  const vault = useAppStore((s) => s.unlockedVault)
  const updateVaultData = useAppStore((s) => s.updateVaultData)

  const existingFolder = folderId
    ? vault?.data.folders.find((f) => f.id === folderId)
    : null

  const [name, setName] = useState(existingFolder?.name || '')
  const [parentId, setParentId] = useState<string | null>(
    existingFolder?.parentId ?? null,
  )
  // New folders get their id up front so the preview card shows their colour.
  const [newId] = useState(() => nanoid())

  if (!vault) return null

  const isEditing = !!existingFolder

  // Exclude the folder itself and its descendants from parent options
  const getValidParentOptions = () => {
    if (!folderId) return vault.data.folders
    const childIds = new Set<string>()
    const addChildren = (id: string) => {
      childIds.add(id)
      vault.data.folders
        .filter((f) => f.parentId === id)
        .forEach((f) => addChildren(f.id))
    }
    addChildren(folderId)
    return vault.data.folders.filter((f) => !childIds.has(f.id))
  }

  const handleSave = () => {
    if (!name.trim()) return
    const now = new Date().toISOString()

    if (isEditing && existingFolder) {
      updateVaultData((data) => ({
        ...data,
        folders: data.folders.map((f) =>
          f.id === existingFolder.id
            ? { ...f, name: name.trim(), parentId }
            : f,
        ),
      }))
    } else {
      updateVaultData((data) => ({
        ...data,
        folders: [
          ...data.folders,
          { id: newId, name: name.trim(), parentId, createdAt: now },
        ],
      }))
    }
    close()
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
    >
      <div className="mb-5 flex items-center gap-3">
        <h2 className="flex-1 font-display text-xl font-semibold tracking-tight">
          {isEditing ? 'Rename folder' : 'New folder'}
        </h2>
        <button
          type="button"
          className="icon-btn"
          aria-label="Close"
          onClick={close}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Live preview of the wallet card */}
      <div
        className="gcard mb-5 flex min-h-[60px] items-center gap-3 rounded-3xl px-4"
        style={gradientStyle(folderId ?? newId)}
        aria-hidden="true"
      >
        <Folder className="h-[18px] w-[18px] opacity-90" />
        <span className="truncate font-display text-[15.5px] font-semibold">
          {name.trim() || 'Folder name'}
        </span>
      </div>

      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="folder-name">
            Name
          </label>
          <input
            id="folder-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            placeholder="e.g. Reading"
            autoFocus
          />
        </div>
        <div>
          <label className="label" htmlFor="folder-parent">
            Inside
          </label>
          <select
            id="folder-parent"
            value={parentId || ''}
            onChange={(e) => setParentId(e.target.value || null)}
            className="input"
          >
            <option value="">Top level</option>
            {getValidParentOptions().map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={close} className="btn-secondary">
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim()}
          className="btn-primary sm:min-w-[120px]"
        >
          {isEditing ? 'Save' : 'Create folder'}
        </button>
      </div>
    </form>
  )
}
